import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { Card, FormGroup, InputWithUnit } from '../components/ui/Card';
import { ProfileForm } from '../components/profiles/ProfileForm';
import type { ProfileFormData } from '../components/profiles/ProfileForm';
import { useProfileDetail, useProfiles } from '../hooks/useProfiles';
import { useUserSettings } from '../hooks/useUserSettings';
import { useProperties } from '../hooks/useProperties';
import { apiFetch } from '../lib/api';
import { fmtDollarInt } from '../lib/format';
import { getDefaultInputs, calculateAll } from '../lib/calculator';
import { getPaymentIndicator, getCashIndicator } from '../lib/price-levels';
import { detectCounty } from '../lib/mn-data';
import { geocodeAddress } from '../hooks/useGooglePlaces';
import { AddressAutocomplete } from '../components/ui/AddressAutocomplete';
import type { BuyerProfile, Property } from '../lib/types';
import { ProfilePageSkeleton } from '../components/ui/Skeletons';

function AddPropertyForm({ onSubmit, onCancel }: {
  onSubmit: (data: { address: string; city: string; zip: string; county: string; askingPrice: number; notes: string }) => void;
  onCancel: () => void;
}) {
  const [address, setAddress] = useState('');
  const [addressText, setAddressText] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  const [askingPrice, setAskingPrice] = useState(350000);
  const [notes, setNotes] = useState('');
  const [nonMnWarning, setNonMnWarning] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(true);

  const applyCountyDetection = (a: string, c: string, z: string) => {
    const detected = detectCounty(a, c, z);
    if (detected) setCounty(detected.countyName);
  };

  const handleSubmit = async () => {
    if (askingPrice <= 0) return;
    const rawForGeocode = address.trim()
      ? [address.trim(), city.trim(), zip.trim()].filter(Boolean).join(', ')
      : addressText.trim();
    if (!rawForGeocode) {
      setGeocodeError('Please enter an address.');
      return;
    }
    setIsGeocoding(true);
    setGeocodeError(null);
    try {
      const normalized = await geocodeAddress(rawForGeocode);
      if (!normalized) {
        setGeocodeError('Address could not be verified. Please select a suggestion from the dropdown or check your entry.');
        return;
      }
      const finalAddress = normalized.streetAddress;
      const finalCity = normalized.city || city.trim();
      const finalZip = normalized.zip || zip.trim();
      setAddress(finalAddress);
      setCity(finalCity);
      setZip(finalZip);
      setNonMnWarning(normalized.state !== 'MN');
      const finalCounty = county.trim() || (() => {
        const det = detectCounty(finalAddress, finalCity, finalZip);
        return det ? det.countyName : '';
      })();
      onSubmit({ address: finalAddress, city: finalCity, zip: finalZip, county: finalCounty, askingPrice, notes: notes.trim() });
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <Card title="Add Property">
      <FormGroup label="Address">
        <AddressAutocomplete
          onLoadingChange={setPlacesLoading}
          onAddressTyped={(value) => {
            setAddressText(value);
            // Clear the parsed address so submit falls back to geocoding the typed text
            setAddress('');
            setGeocodeError(null);
          }}
          onPlaceSelected={(parsed, _formatted) => {
            setAddress(parsed.streetAddress);
            setAddressText(parsed.streetAddress);
            setCity(parsed.city);
            setZip(parsed.zip);
            setNonMnWarning(parsed.state !== 'MN');
            setGeocodeError(null);
            applyCountyDetection(parsed.streetAddress, parsed.city, parsed.zip);
          }}
        />
      </FormGroup>
      {nonMnWarning && (
        <div className="address-detected" style={{ background: '#fefce8', borderColor: '#fde68a', color: '#854d0e', marginBottom: '0.5rem' }}>
          <span className="detect-icon">⚠️</span>
          <span>This address is outside Minnesota. County and tax rate won't be auto-detected — enter them manually.</span>
        </div>
      )}
      {geocodeError && (
        <div className="address-detected" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginBottom: '0.5rem' }}>
          <span className="detect-icon">✕</span>
          <span>{geocodeError}</span>
        </div>
      )}
      <div className="form-row">
        <FormGroup label="City">
          <input
            type="text"
            value={city}
            onChange={e => { setCity(e.target.value); applyCountyDetection(address, e.target.value, zip); }}
            placeholder="Minneapolis"
          />
        </FormGroup>
        <FormGroup label="ZIP Code">
          <input
            type="text"
            value={zip}
            onChange={e => { setZip(e.target.value); applyCountyDetection(address, city, e.target.value); }}
            placeholder="55401"
          />
        </FormGroup>
        <FormGroup label="County">
          <input type="text" value={county} onChange={e => setCounty(e.target.value)} placeholder="Hennepin" />
        </FormGroup>
      </div>
      <FormGroup label="Asking Price">
        <InputWithUnit unit="$" value={askingPrice} onChange={setAskingPrice} min={0} step={5000} />
      </FormGroup>
      <FormGroup label="Notes (optional)">
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="MLS link, notes about the property..." />
      </FormGroup>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={askingPrice <= 0 || isGeocoding || placesLoading}
          onClick={handleSubmit}
        >
          {isGeocoding ? 'Verifying…' : 'Add Property'}
        </button>
      </div>
    </Card>
  );
}

function CopyPropertyPicker({
  currentProfileId,
  profiles,
  getToken,
  onCopy,
  onCancel,
  copying,
}: {
  currentProfileId: string;
  profiles: BuyerProfile[];
  getToken: () => Promise<string | null>;
  onCopy: (prop: Property) => void;
  onCancel: () => void;
  copying: boolean;
}) {
  const otherProfiles = profiles.filter(p => p.id !== currentProfileId);
  const [sourceProfileId, setSourceProfileId] = useState(otherProfiles[0]?.id ?? '');
  const [sourceProps, setSourceProps] = useState<Property[]>([]);
  const [loadingProps, setLoadingProps] = useState(false);

  useEffect(() => {
    if (!sourceProfileId) return;
    let cancelled = false;
    setLoadingProps(true);
    apiFetch<Property[]>(`/profiles/${sourceProfileId}/properties`, {}, getToken)
      .then(data => { if (!cancelled) setSourceProps(data); })
      .catch(() => { if (!cancelled) setSourceProps([]); })
      .finally(() => { if (!cancelled) setLoadingProps(false); });
    return () => { cancelled = true; };
  }, [sourceProfileId, getToken]);

  if (otherProfiles.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            No other buyer profiles found. Create a second profile first, then copy properties between them.
          </p>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="card-header">Copy Property from Another Profile</div>
      <div className="card-body">
        {/* Source profile selector */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
            Source Profile
          </label>
          <select
            value={sourceProfileId}
            onChange={e => { setSourceProfileId(e.target.value); setSourceProps([]); }}
            style={{ width: '100%' }}
            disabled={copying}
          >
            {otherProfiles.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Property list */}
        {loadingProps ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0.5rem 0' }}>Loading properties…</div>
        ) : sourceProps.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0.5rem 0' }}>
            No properties saved in this profile.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {sourceProps.map(prop => (
              <div
                key={prop.id}
                onClick={() => !copying && onCopy(prop)}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '0.65rem 0.9rem',
                  cursor: copying ? 'not-allowed' : 'pointer',
                  opacity: copying ? 0.6 : 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (!copying) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-subtle, rgba(0,0,0,0.03))'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{prop.address}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {[prop.city, prop.county ? `${prop.county} County` : ''].filter(Boolean).join(', ')}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                  <div style={{ fontWeight: 700 }}>{fmtDollarInt(prop.askingPrice)}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--primary)', fontWeight: 600 }}>Copy →</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ textAlign: 'right' }}>
          <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={copying}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ProfileContent() {
  const { profileId } = useParams();
  const { getToken, userId } = useAuth();
  const { profile, loading: profileLoading, error: profileError, refetch, updateProfile } = useProfileDetail(profileId, getToken);
  const { properties, loading: propsLoading, createProperty, updateProperty } = useProperties(profileId, getToken);
  const { profiles: allProfiles } = useProfiles(getToken);
  const { defaultProfileId, setDefaultProfile } = useUserSettings(getToken);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [copying, setCopying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const navigate = useNavigate();

  // Track the most-recently-visited profile so the share flow can default to it
  useEffect(() => {
    if (profileId) localStorage.setItem('lastUsedProfileId', profileId);
  }, [profileId]);

  const [sortBy, setSortBy] = useState<'added' | 'price-asc' | 'price-desc' | 'affordability'>('added');
  const [filterCity, setFilterCity] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Quick affordability estimate per property using profile defaults (no saved analysis needed)
  const propertyAffordability = useMemo(() => {
    type AffData = {
      monthlyPayment: number;
      cashToClose: number;
      payIndicator: 'green' | 'yellow' | 'red';
      cashIndicator: 'green' | 'yellow' | 'red';
      score: number; // 2=green, 1=yellow, 0=red (min of both indicators)
    };
    const map = new Map<string, AffData>();
    if (!profile || properties.length === 0) return map;

    const downPct = profile.defaultDownPaymentPct ?? 10;
    const combinedIncome = profile.grossAnnualIncome + (profile.coBorrower?.grossAnnualIncome ?? 0);
    const combinedDebts = profile.monthlyDebts + (profile.coBorrower?.monthlyDebts ?? 0);
    const grossMonthly = combinedIncome / 12;
    const indScore = (ind: 'green' | 'yellow' | 'red') => ind === 'green' ? 2 : ind === 'yellow' ? 1 : 0;

    for (const prop of properties) {
      const inputs = {
        ...getDefaultInputs(),
        homePrice: prop.askingPrice,
        downPaymentPct: downPct,
        downPaymentAmt: Math.round(prop.askingPrice * (downPct / 100)),
        creditScore: profile.creditScoreRange,
        grossAnnualIncome: combinedIncome,
        // Profile monthlyDebts is already a total — place in monthlyOtherDebt
        monthlyOtherDebt: combinedDebts,
        monthlyCarPayment: 0,
        monthlyStudentLoans: 0,
        monthlyCreditCards: 0,
      };
      const res = calculateAll(inputs);
      const monthlyPayment = res.totalMonthly;
      const cashToClose = res.cashToClose.cashToClose;
      const payIndicator = getPaymentIndicator(monthlyPayment, grossMonthly);
      const cashIndicator = getCashIndicator(cashToClose, profile.cashOnHand);
      map.set(prop.id, {
        monthlyPayment,
        cashToClose,
        payIndicator,
        cashIndicator,
        score: Math.min(indScore(payIndicator), indScore(cashIndicator)),
      });
    }
    return map;
  }, [properties, profile]);

  const cities = useMemo(() =>
    [...new Set(properties.map(p => p.city).filter(Boolean))].sort(),
  [properties]);

  const sortedProperties = useMemo(() => {
    let arr = [...properties];
    // Filter before sorting
    if (!showArchived) arr = arr.filter(p => !p.archived);
    if (filterCity) arr = arr.filter(p => p.city === filterCity);
    // Sort
    if (sortBy === 'price-asc') arr.sort((a, b) => a.askingPrice - b.askingPrice);
    else if (sortBy === 'price-desc') arr.sort((a, b) => b.askingPrice - a.askingPrice);
    else if (sortBy === 'affordability') {
      arr.sort((a, b) => {
        const aScore = propertyAffordability.get(a.id)?.score ?? 0;
        const bScore = propertyAffordability.get(b.id)?.score ?? 0;
        return bScore !== aScore ? bScore - aScore : a.askingPrice - b.askingPrice;
      });
    }
    return arr;
  }, [properties, sortBy, propertyAffordability, showArchived, filterCity]);

  const loading = profileLoading || propsLoading;

  if (loading) return <ProfilePageSkeleton />;

  if (profileError || !profile) {
    return (
      <div className="page-container">
        <h2 className="page-title">Buyer Profile</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          {profileError || 'Profile not found'}
        </div>
        <Link to="/dashboard" className="btn btn-secondary" style={{ marginTop: '1rem', display: 'inline-block' }}>← Back to Dashboard</Link>
      </div>
    );
  }

  const isOwner = profile.ownerUserId === userId;
  const isDefault = defaultProfileId === profile.id;
  const members = (profile as any).members as Array<{ userId: string; email: string; name: string; role: string; joinedAt: string }> | undefined;
  const pendingInvitations = (profile as any).pendingInvitations as Array<{ email: string; createdAt: string }> | undefined;
  const co = profile.coBorrower;

  const handleAddProperty = async (data: { address: string; city: string; zip: string; county: string; askingPrice: number; notes: string }) => {
    try {
      const { property: prop, alreadySaved } = await createProperty(data);
      setShowAddProperty(false);
      if (alreadySaved) {
        // The API deduplicated this address — it was already saved to this profile
        // (either by this user or another member). Navigate to the existing record.
        navigate(`/profiles/${profileId}/properties/${prop.id}`, {
          state: { banner: 'This address was already saved to your profile. Showing the existing property.' },
        });
      } else {
        navigate(`/profiles/${profileId}/properties/${prop.id}`);
      }
    } catch (err: any) {
      alert(`Failed to add property: ${err.message}`);
    }
  };

  const handleArchiveProperty = async (propId: string, archived: boolean) => {
    try {
      await updateProperty(propId, { archived });
    } catch (err: any) {
      alert(`Failed to ${archived ? 'archive' : 'unarchive'} property: ${err.message}`);
    }
  };

  const handleCopyProperty = async (sourceProp: Property) => {
    setCopying(true);
    try {
      const { property: prop, alreadySaved } = await createProperty({
        address: sourceProp.address,
        city: sourceProp.city,
        zip: sourceProp.zip,
        county: sourceProp.county,
        askingPrice: sourceProp.askingPrice,
        notes: sourceProp.notes,
      });
      setShowCopyPicker(false);
      if (alreadySaved) {
        navigate(`/profiles/${profileId}/properties/${prop.id}`, {
          state: { banner: 'This property is already saved to this profile.' },
        });
      } else {
        navigate(`/profiles/${profileId}/properties/${prop.id}`);
      }
    } catch (err: any) {
      alert(`Failed to copy property: ${err.message}`);
    } finally {
      setCopying(false);
    }
  };

  const handleUpdate = async (data: ProfileFormData) => {
    try {
      await updateProfile(data);
      await refetch();
      setIsEditing(false);
    } catch (err: any) {
      alert(`Failed to update profile: ${err.message}`);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !profileId) return;
    setInviting(true);
    try {
      await apiFetch(`/profiles/${profileId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim() }),
      }, getToken);
      setInviteEmail('');
      await refetch();
    } catch (err: any) {
      alert(`Failed to send invite: ${err.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (email: string) => {
    if (!profileId) return;
    setRevoking(email);
    try {
      await apiFetch(`/profiles/${profileId}/invitations/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }, getToken);
      await refetch();
    } catch (err: any) {
      alert(`Failed to revoke invite: ${err.message}`);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="page-container">
      <Link to="/dashboard" style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none' }}>&larr; Dashboard</Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1rem 0' }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>{profile.name}</h2>
          {isDefault && (
            <span style={{ fontSize: '0.75rem', color: 'var(--primary)' }}>⭐ Default profile</span>
          )}
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setDefaultProfile(isDefault ? null : profile.id)}
        >
          {isDefault ? 'Remove Default' : 'Set as Default'}
        </button>
      </div>

      {/* Financial Summary / Edit Form */}
      {isEditing ? (
        <ProfileForm
          title="Edit Profile"
          submitLabel="Save Changes"
          initialValues={profile}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Financial Summary</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>Edit</button>
          </div>
          <div className="card-body">
            <div className="financial-summary-grid">
              <div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--primary)' }}>{fmtDollarInt(profile.cashOnHand)}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cash on Hand</div>
              </div>
              <div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                  {profile.defaultDownPaymentPct ?? 10}%
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Default Down Payment</div>
              </div>
              <div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                  {fmtDollarInt(profile.grossAnnualIncome + (co?.grossAnnualIncome ?? 0))}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {co ? 'Combined Annual Income' : 'Annual Income'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                  {fmtDollarInt(profile.monthlyDebts + (co?.monthlyDebts ?? 0))}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {co ? 'Combined Monthly Debts' : 'Monthly Debts'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>
                  {co
                    ? `${Math.min(parseInt(profile.creditScoreRange), parseInt(co.creditScoreRange))}+`
                    : `${profile.creditScoreRange}+`}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {co ? 'Qualifying Credit Score' : 'Credit Score'}
                </div>
              </div>
            </div>

            {co && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Co-Borrower:</span>
                <span><strong>{co.name || 'Partner'}</strong></span>
                <span><span style={{ color: 'var(--text-muted)' }}>Income: </span>{fmtDollarInt(co.grossAnnualIncome)}/yr</span>
                <span><span style={{ color: 'var(--text-muted)' }}>Debts: </span>{fmtDollarInt(co.monthlyDebts)}/mo</span>
                <span><span style={{ color: 'var(--text-muted)' }}>Credit: </span>{co.creditScoreRange}+</span>
              </div>
            )}

            {profile.use401k && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                401(k) loan: <strong style={{ color: 'var(--primary)' }}>{fmtDollarInt(profile.loan401kAmount ?? 25000)}</strong> at {profile.loan401kRate ?? 5.5}% · {profile.loan401kTerm ?? 5} yr repayment
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members & Access (collapsible) */}
      <button
        onClick={() => setShowMembers(v => !v)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          width: '100%', margin: '1.5rem 0 0', padding: '0.5rem 0',
          background: 'none', border: 'none', cursor: 'pointer',
          borderTop: '1px solid var(--border)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 500 }}>Members & Access</h3>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{showMembers ? '▲' : '▼'}</span>
      </button>
      {showMembers && (
      <div className="card" style={{ marginTop: '0.5rem' }}>
        <div className="card-body">
          {/* Existing members */}
          {members && members.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              {members.map(m => {
                const isCurrentUser = m.userId === userId;
                const primaryDisplay = m.name || m.email || (isCurrentUser ? 'You' : 'Unknown Member');
                const showEmail = !!(m.name && m.email);
                return (
                  <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{primaryDisplay}</strong>
                      {showEmail && (
                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem', fontSize: '0.82rem' }}>{m.email}</span>
                      )}
                      {isCurrentUser && (
                        <span style={{ color: 'var(--primary)', marginLeft: '0.4rem', fontSize: '0.75rem', fontWeight: 600 }}>(You)</span>
                      )}
                    </span>
                    <span style={{
                      flexShrink: 0,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      padding: '2px 7px',
                      borderRadius: '999px',
                      background: m.role === 'owner' ? 'rgba(99,102,241,0.12)' : 'rgba(107,114,128,0.12)',
                      color: m.role === 'owner' ? '#6366f1' : 'var(--text-muted)',
                      textTransform: 'capitalize',
                    }}>
                      {m.role}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pending invitations (owner only) */}
          {isOwner && pendingInvitations && pendingInvitations.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pending Invitations</div>
              {pendingInvitations.map(inv => (
                <div key={inv.email} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                  <span style={{ flex: 1, color: 'var(--text-muted)' }}>{inv.email}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Invited {new Date(inv.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    className="btn btn-sm btn-secondary"
                    disabled={revoking === inv.email}
                    onClick={() => handleRevoke(inv.email)}
                    style={{ fontSize: '0.72rem' }}
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Invite form (owner only) */}
          {isOwner && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.25rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Invite by email
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="partner@example.com"
                  onKeyDown={e => e.key === 'Enter' && handleInvite()}
                  style={{ width: '100%' }}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!inviteEmail.trim() || inviting}
                onClick={handleInvite}
              >
                {inviting ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          )}

          {!isOwner && (!members || members.length === 0) && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No members listed.</div>
          )}
        </div>
      </div>
      )}

      {/* Properties Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.5rem 0 0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Saved Properties</h3>
        {properties.length > 0 && !showAddProperty && !showCopyPicker && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setShowCopyPicker(true); setShowAddProperty(false); }}
            >
              ⊕ Copy from Profile
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { setShowAddProperty(true); setShowCopyPicker(false); }}
            >
              + Add Property
            </button>
          </div>
        )}
      </div>

      {/* Unified list controls: sort + city filter + archived toggle */}
      {(properties.length > 1 || properties.some(p => p.archived)) && !showAddProperty && !showCopyPicker && (
        <div className="list-controls">
          {/* Sort group */}
          {properties.length > 1 && (
            <div className="list-controls-group">
              <span className="list-controls-label">Sort</span>
              {([
                { key: 'added'         as const, label: 'Recent'       },
                { key: 'affordability' as const, label: '💰 Affordable' },
                { key: 'price-asc'     as const, label: 'Price ↑'      },
                { key: 'price-desc'    as const, label: 'Price ↓'      },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  className={`filter-chip${sortBy === key ? ' active' : ''}`}
                  onClick={() => setSortBy(key)}
                >{label}</button>
              ))}
            </div>
          )}
          {/* City filter — only when 2+ cities */}
          {cities.length >= 2 && (
            <>
              {properties.length > 1 && <div className="list-controls-divider" />}
              <div className="list-controls-group">
                <span className="list-controls-label">City</span>
                <button
                  className={`filter-chip${filterCity === null ? ' active' : ''}`}
                  onClick={() => setFilterCity(null)}
                >All</button>
                {cities.map(city => (
                  <button
                    key={city}
                    className={`filter-chip${filterCity === city ? ' active' : ''}`}
                    onClick={() => setFilterCity(filterCity === city ? null : city)}
                  >{city}</button>
                ))}
              </div>
            </>
          )}
          {/* Archived toggle */}
          {properties.some(p => p.archived) && (
            <>
              <div className="list-controls-divider" />
              <label className="archived-toggle" style={{ marginTop: 0 }}>
                <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                Archived ({properties.filter(p => p.archived).length})
              </label>
            </>
          )}
        </div>
      )}

      {showCopyPicker && profileId && (
        <CopyPropertyPicker
          currentProfileId={profileId}
          profiles={allProfiles}
          getToken={getToken}
          onCopy={handleCopyProperty}
          onCancel={() => setShowCopyPicker(false)}
          copying={copying}
        />
      )}

      {showAddProperty && (
        <div style={{ marginBottom: '1rem' }}>
          <AddPropertyForm onSubmit={handleAddProperty} onCancel={() => setShowAddProperty(false)} />
        </div>
      )}

      {properties.length === 0 && !showAddProperty && !showCopyPicker ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏡</div>
          <div className="empty-state-text">No properties saved yet</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Add a property to start analyzing offer prices and track your home search.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => setShowAddProperty(true)}>
              Add Your First Property
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowCopyPicker(true)}
            >
              ⊕ Copy from Profile
            </button>
          </div>
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {sortedProperties.map(prop => {
            const aff = propertyAffordability.get(prop.id);
            // score: 2=green, 1=yellow, 0=red
            const worstInd = aff ? (['red', 'yellow', 'green'] as const)[aff.score] : undefined;
            return (
              <div
                key={prop.id}
                className={`card${prop.archived ? ' archived' : ''}`}
                style={{ cursor: 'pointer', transition: 'box-shadow 0.15s', touchAction: 'pan-y' }}
                onClick={() => navigate(`/profiles/${profileId}/properties/${prop.id}`)}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
              >
                <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    {/* Overall affordability dot */}
                    {worstInd && (
                      <span
                        className={`indicator indicator-${worstInd}`}
                        style={{ marginTop: '0.3rem', flexShrink: 0 }}
                      />
                    )}
                    {/* Address + city/county + optional notes */}
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {prop.address}
                        {prop.archived && (
                          <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400, verticalAlign: 'middle' }}>
                            archived
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {[prop.city, prop.county ? `${prop.county} County` : ''].filter(Boolean).join(', ')}
                      </div>
                      {prop.notes && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {prop.notes}
                        </div>
                      )}
                    </div>
                    {/* Price + affordability estimates */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>{fmtDollarInt(prop.askingPrice)}</div>
                      {aff && (
                        <>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                            <span className={`indicator indicator-${aff.payIndicator}`} />
                            {' '}~{fmtDollarInt(aff.monthlyPayment)}/mo
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            <span className={`indicator indicator-${aff.cashIndicator}`} />
                            {' '}~{fmtDollarInt(aff.cashToClose)} cash
                          </div>
                        </>
                      )}
                    </div>
                    <span style={{ color: 'var(--text-muted)', fontSize: '1.1rem', lineHeight: 1, alignSelf: 'center', flexShrink: 0 }}>›</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* Empty filter message */}
        {sortedProperties.length === 0 && properties.length > 0 && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center', padding: '1.5rem 0' }}>
            No properties match the current filter.
          </div>
        )}
        {/* Affordability legend */}
        {profile && properties.length > 0 && sortedProperties.length > 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Estimates based on profile defaults</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <span><span className="indicator indicator-green" /> Affordable</span>
              <span><span className="indicator indicator-yellow" /> Tight</span>
              <span><span className="indicator indicator-red" /> Over budget</span>
            </span>
          </div>
        )}
        </>
      )}
    </div>
  );
}

export function ProfilePage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Buyer Profile</h2>
        <div className="note-box">
          Authentication is required to view profiles.
          Configure <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable authentication.
        </div>
      </div>
    );
  }

  return <ProfileContent />;
}
